
interface DomElement extends Element {
  msMatchesSelector(selectors: string): boolean
  parentNode: DomElement
}

function closest(el: DomElement, selector: string): Element {
  const matches = Element.prototype.matches ? el.matches(selector) : el.msMatchesSelector(selector)
  if (!matches && el.parentNode.nodeName === 'BODY') {
    return null
  }
  return matches ? el : closest(el.parentNode, selector)
}


function elementIsVisible(elem: HTMLElement): boolean {
  return elem && elem.offsetWidth > 0 && elem.offsetHeight > 0
}

function getLikelyTabbableElems(parent: Element) {
  return parent.querySelectorAll('[tabindex],a,input,select,textarea,button,object')
}

// The checkboxes and radio buttons are hidden, so not really visible, even though there
// is a radio/checkbox styled element on the screen.  So, need to do a special check
// for them.  Going to assume they are tabbable if the parent is visible
function isTabbableCheckboxOrRadio(elem) {
  const nodeName = elem.nodeName.toLowerCase()
  return nodeName === 'input' && (elem.type === 'radio' || elem.type === 'checkbox') &&
    elementIsVisible(elem.parentNode)
}

function elemHasTabIndex(elem) {
  // The checkboxes and radio buttons are hidden, so not really visible, even though there
  // is a radio/checkbox styled element on the screen.  So, need to do a special check
  // for them
  if (elementIsVisible(elem) || isTabbableCheckboxOrRadio(elem)) {
    const tabIndex = elem.getAttribute('tabindex')
    const nodeName = elem.nodeName.toLowerCase()
    let hasTabindex = tabIndex !== null && tabIndex >= 0
    if (/^(input|select|textarea|button|object)$/.test(nodeName)) {
      hasTabindex = !elem.disabled
      // NOTE: Not checking for disabled fieldsets. Add later if needed. The following comment
      // is from jQuery:
      // Form controls within a disabled fieldset are disabled.
      // However, controls within the fieldset's legend do not get disabled.
      // Since controls generally aren't placed inside legends, we skip
      // this portion of the check.
    } else if (nodeName === 'a') {
      hasTabindex = elem.href || hasTabindex
    }
    return hasTabindex
  }
  return false
}

function scrollTo(x: number, y: number): void {
  // for browsers that don't support smooth scroll (e.g. Legacy Edge, Safari iOS/desktop)
  if (!('scrollBehavior' in document.documentElement.style)) {
    window.scrollTo(x, y)
  } else {
    window.scrollTo({ top: y, left: x, behavior: 'smooth' })
  }
}

function scrollToElement(el: Element): void {
  if (el) {
    el.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' })
  }
}

function focus(elOrFunc: HTMLElement | (() => HTMLElement), maxAttempts = 10): Promise<void> {
  return whenElementIsVisible(elOrFunc, maxAttempts).then((visibleEl: HTMLElement) => {
    visibleEl.focus()
  })
}

function whenElementIsVisible(
  elOrFunc: HTMLElement | (() => HTMLElement), maxAttempts = 10, attempt = 1
): Promise<void | Element> {
  return new Promise((resolve, reject) => {
    // elOrFunc is an element or a function.  If a function, it is called to get the el. This is
    // to handle cases where the method of retrieving the el must continue to be called.  The
    // most obvious case is a $refs reference that is not yet rendered
    const el = typeof elOrFunc === 'function' ? elOrFunc() : elOrFunc
    if (elementIsVisible(el)) {
      resolve(el)
    } else if (attempt < maxAttempts) {
      window.requestAnimationFrame(() => {
        whenElementIsVisible(elOrFunc, maxAttempts, attempt + 1)
          .then(visibleEl => resolve(visibleEl)).catch(() => reject())
      })
    } else {
      reject()
    }
  })
}

function getRefEl(refs, refName) {
  const ref = refs[refName]
  if (!ref) {
    return null
  }
  return ref.tagName ? ref : ref.$el
}

function whenRefIsVisible(
  refs: Record<string, unknown>, refName: string, maxAttempts = 10
): Promise<void | Element> {
  return whenElementIsVisible(() => getRefEl(refs, refName), maxAttempts)
}

let domReadyPromise = null

export default {

  /**
   * Same functionality as jQuery.closest where the element is passed in instead of called on
   * and it returns null if closest not found.
   * @param el - element to find closest for
   * @param selector - selector being searched for to be closest
   */
  closest,

  /**
   * Determines whether the given element is visible or not
   * @param - the element
   */
  elementIsVisible,

  /**
   * Returns an array of elements within the given parent element that are tabbable.
   * Basics of implementation are from jQuery.
   */
  getTabbableElements: (parent: Element): Array<Element> => {
    const likelyTabbableNodeList = getLikelyTabbableElems(parent)
    const tabbableElements = []
    for (let i = 0; i < likelyTabbableNodeList.length; i++) {
      const elem = likelyTabbableNodeList[i]
      // The checkboxes and radio buttons are hidden, so not really visible, even though there
      // is a radio/checkbox styled element on the screen.  So, need to do a special check
      // for them
      if (elemHasTabIndex(elem)) {
        tabbableElements.push(elem)
      }
    }
    return tabbableElements
  },

  /**
   * Creates a div with the given id and appends to the body if it doesn't already exist
   * @param id
   */
  appendDivToBody(id: string): void {
    if (!document.getElementById(id)) {
      const container = document.createElement('div')
      container.id = id
      document.body.appendChild(container)
    }
  },

  /**
   * Removes child element of body with the given id
   * @param id
   */
  removeFromBody(id: string): void {
    const container = document.getElementById(id)
    if (container) {
      document.body.removeChild(container)
    }
  },

  onDomReady(): Promise<void> {
    if (domReadyPromise) {
      return domReadyPromise
    }
    domReadyPromise = new Promise<void>((resolve) => {
      if (document.readyState === 'complete' || document.readyState === 'interactive') {
        resolve()
      } else {
        document.addEventListener('DOMContentLoaded', () => resolve())
      }
    })
    return domReadyPromise
  },

  /**
   * Foucs on the given element when it is visible.
   * @param el
   * @param maxAttempts (optional with default of 10) - max attempts at checking
   */
  focus,

  /**
   * Foucs on the given element when it is visible. This does not return the Promise and
   * swallows the exception if fails to focus.  Use 'focus' if the Promise is needed.
   * @param el
   * @param maxAttempts (optional with default of 10) - max attempts at checking
   */
  safeFocus: (elOrFunc: HTMLElement | (() => HTMLElement), maxAttempts = 10): void => {
    focus(elOrFunc, maxAttempts).catch(() => {})
  },

  /**
   * Foucs on the given element when it is visible. This does not return the Promise and
   * swallows the exception if it fails to focus.
   * @param refs - this would be this.$refs inside a Vue component
   * @param refName - the name of the ref, called like this.$refs[refName]
   * @param maxAttempts (optional with default of 10) - max attempts at checking
   */
  safeFocusRef: (
    refs: Record<string, unknown>, refName: string, maxAttempts = 10
  ): Promise<void> => {
    return whenRefIsVisible(refs, refName, maxAttempts).then((visibleEl: HTMLElement) => {
      visibleEl.focus()
    }).catch(() => {})
  },

  /**
   * Scroll down or up to the given element
   */
  scrollToElement,

  scrollToTop: (): void => {
    scrollTo(window.scrollX, 0)
  },

  /**
   * Same as scrollToRef, but will not scroll down, only up
   * @param refs
   * @param refName
   * @param additionalPx - how many pixels to scroll past the given ref element
   * @param animationTimeMillis - how quickly to scroll up in milliseconds
   */
  scrollUpToRef(
    refs: Record<string, unknown>, refName: string, additionalPx = 0): Promise<void> {
    const el = getRefEl(refs, refName)
    if (el) {
      const viewportOffset = el.getBoundingClientRect()
      // these are relative to the viewport, i.e. the window
      const top = viewportOffset.top
      if (top <= 0) {
        return new Promise((resolve) => {
          // top is negative, so like subtracting from how far page is currently scrolled
          const scrollToY = Math.floor(window.pageYOffset + top - (additionalPx || 0))
          scrollTo(window.scrollX, scrollToY)
          // can't know when the smooth scroll is done, so just giving some time
          setTimeout(resolve, 500)
        })
      }
      return Promise.resolve() // already up to top
    }
    return Promise.reject(new Error(`ref, ${refName} is not an element`))
  },

  /**
   * Can be used to set the cursor at the end of text in a text field or textarea as well
   * as a contenteditable div
   * @param el - the element
   */
  setCursorAtEndOfText(el: Node): void {
    const range = document.createRange()
    range.selectNodeContents(el)
    range.collapse(false)
    const sel = window.getSelection()
    sel.removeAllRanges()
    sel.addRange(range)
  },

  /**
   * Returns a promise to resolve when the element becomes visible for the given number of
   * attempts.  An attempt is the time it takes for window.requestAnimationFrame() to be called.
   * It will be rejected if it is not visible after the given number of attempts.
   *
   * @param el - element to check for
   * @param maxAttempts (optional with default of 10) - max attempts at checking
   */
  whenElementIsVisible,

  /**
   * Returns a promise to resolve when the element the ref refers to becomes visible for the given
   * number of attempts.  An attempt is the time it takes for window.requestAnimationFrame() to be
   * called. It will be rejected if it is not visible after the given number of attempts.
   *
   * @param refs - this would be this.$refs inside a Vue component
   * @param refName - the name of the ref, called like this.$refs[refName]
   * @param maxAttempts (optional with default of 10) - max attempts at checking
   */
  whenRefIsVisible
}
